import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { multiLocationService, beverageProductsService, siteInventoryService, stocktakeSessionsService } from '../services';
import ProductPicker from '../components/ProductPicker';
import ConsolidatedBreakdownDialog from '../components/ConsolidatedBreakdownDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Warehouse, MapPin, ChevronRight, Users, CheckCircle, AlertCircle,
  Loader2, ArrowRightLeft, Plus, Trash2, Package, Clock, Edit2,
  BarChart3, Eye, Send, ChevronDown, ChevronUp, RefreshCw, Calculator,
  Layers, FileText,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';
import StocktakeReportsDialog from '../components/StocktakeReportsDialog';
import { buildOfflineConsolidatedReview, aggregateLocationCalcs } from '../services/offlineConsolidatedReview';
import { buildMultiLocBreakdown } from '../services/multiLocBreakdownBuilder';
import { StockAuditBreakdownDialog } from './panels/StockAuditBreakdownDialog';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  locked: 'bg-amber-100 text-amber-800',
};

const fmtCurrency = (v) => {
  if (v == null) return '\u00A30.00';
  const n = Number(v);
  if (n < 0) return `(\u00A3${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
  return `\u00A3${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Parent Audit Dashboard ──────────────────────────

export function ParentAuditDashboard({ session, auditors = [], isOffline = false }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [children, setChildren] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [childData, transferData] = await Promise.all([
        multiLocationService.listChildSessions(session.id),
        multiLocationService.listTransfers(session.id).catch(() => []),
      ]);
      setChildren(childData || []);
      setTransfers(transferData || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [session.id, toast]);

  useEffect(() => { load(); }, [load]);

  // iter422 — Centralised consolidated/per-location review loader.
  // Works online (API → /consolidated-review) and offline (rebuilds the
  // same shape from each child's locally-saved calculation_snapshot).
  const loadReview = useCallback(async () => {
    setReviewLoading(true);
    try {
      const data = isOffline
        ? await buildOfflineConsolidatedReview(session.id)
        : await multiLocationService.getConsolidatedReview(session.id);
      setReviewData(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load consolidated review', variant: 'destructive' });
    } finally { setReviewLoading(false); }
  }, [session.id, toast, isOffline]);

  // iter439e — Cascade refresh. The "Refresh" button on the Calculate &
  // Results panel previously only re-fetched `/consolidated-review`, so
  // a stale child snapshot kept polluting the aggregate. Now it
  // recalculates every child in sequence and THEN re-loads the review.
  // Release Contract S2 — a child with 0 closing count entries is SKIPPED
  // (not silently calculated) so an empty location never pollutes the
  // consolidated figures with a phantom all-zero snapshot.
  const [reloadCascading, setReloadCascading] = useState(false);
  const cascadeRecalcAndReload = useCallback(async () => {
    setReloadCascading(true);
    try {
      const calculable = (children || []).filter(c => c.id && (c.counted_lines ?? 1) > 0);
      const skipped = (children || []).filter(c => c.id && (c.counted_lines ?? 1) === 0);
      if (calculable.length > 0) {
        toast({
          title: 'Recalculating',
          description: `Refreshing ${calculable.length} location${calculable.length === 1 ? '' : 's'} before consolidating…`
            + (skipped.length > 0 ? ` (${skipped.length} skipped — no closing counts yet)` : ''),
        });
        for (const c of calculable) {
          try {
            await stocktakeSessionsService.calculate(c.id, 'wac');
          } catch (e) {
            console.warn(`[cascade] child ${c.id} calc failed:`, e);
          }
        }
      } else if (skipped.length > 0) {
        toast({
          title: 'Nothing to recalculate',
          description: `${skipped.length} location${skipped.length === 1 ? '' : 's'} ${skipped.length === 1 ? 'has' : 'have'} no closing counts yet.`,
          variant: 'destructive',
        });
      }
      await loadReview();
    } finally {
      setReloadCascading(false);
    }
  }, [children, loadReview, toast]);

  // iter422 — Load the review payload once the children list is in so
  // the Calculate & Results tab is ready before the user clicks it.
  useEffect(() => {
    if (!loading && children.length > 0 && !reviewData && !reviewLoading) {
      loadReview();
    }
    // We deliberately don't depend on reviewData/reviewLoading here —
    // we only want this to fire when children first arrive. loadReview
    // is wrapped in useCallback so its identity is stable per session.
  }, [loading, children.length, loadReview, reviewData, reviewLoading]);

  // iter382 — "Refresh from Master" on the parent dashboard. The
  // wizard-level button covers each child individually; at the parent
  // level we run heal on EVERY child + the parent itself so a single
  // click fixes the whole audit (transfers + count snapshots + costs).
  const [healing, setHealing] = useState(false);
  const handleHealAll = useCallback(async () => {
    if (healing) return;
    if (session.status === 'locked') {
      toast({ title: 'Locked', description: 'Unlock the audit before refreshing.', variant: 'destructive' });
      return;
    }
    setHealing(true);
    try {
      // Heal parent first (transfers + parent count sheet snapshots).
      const parentResult = await stocktakeSessionsService.healSessionSnapshots(session.id);
      let agg = {
        names_updated: parentResult.names_updated || 0,
        units_updated: parentResult.units_updated || 0,
        entry_units_filled: parentResult.entry_units_filled || 0,
        costs_filled: parentResult.costs_filled || 0,
        transfer_lines_healed: parentResult.transfer_lines_healed || 0,
        sessions_scanned: 1,
        skipped: 0,
      };
      // Heal each editable child. Locked / completed children skip
      // (the endpoint returns 409 — we ignore and continue).
      for (const child of (children || [])) {
        if (['locked', 'cancelled'].includes(child.status)) {
          agg.skipped += 1;
          continue;
        }
        try {
          const r = await stocktakeSessionsService.healSessionSnapshots(child.id);
          agg.names_updated += r.names_updated || 0;
          agg.units_updated += r.units_updated || 0;
          agg.entry_units_filled += r.entry_units_filled || 0;
          agg.costs_filled += r.costs_filled || 0;
          // transfer_lines_healed comes from the parent heal — child
          // heals re-cover the same parent transfer set so we DON'T
          // double-count here.
          agg.sessions_scanned += 1;
        } catch (_) {
          agg.skipped += 1;
        }
      }
      const parts = [];
      if (agg.names_updated) parts.push(`${agg.names_updated} name${agg.names_updated !== 1 ? 's' : ''}`);
      if (agg.units_updated) parts.push(`${agg.units_updated} unit${agg.units_updated !== 1 ? 's' : ''}`);
      if (agg.entry_units_filled) parts.push(`${agg.entry_units_filled} entry unit${agg.entry_units_filled !== 1 ? 's' : ''}`);
      if (agg.costs_filled) parts.push(`${agg.costs_filled} cost${agg.costs_filled !== 1 ? 's' : ''}`);
      if (agg.transfer_lines_healed) parts.push(`${agg.transfer_lines_healed} transfer cost${agg.transfer_lines_healed !== 1 ? 's' : ''}`);
      const desc = parts.length
        ? `Refreshed ${parts.join(', ')} across ${agg.sessions_scanned} session${agg.sessions_scanned !== 1 ? 's' : ''}${agg.skipped ? ` (${agg.skipped} skipped — locked/cancelled)` : ''}.`
        : `Already up-to-date across ${agg.sessions_scanned} session${agg.sessions_scanned !== 1 ? 's' : ''}.`;
      toast({ title: 'Master data refreshed', description: desc });
      await load();
    } catch (err) {
      toast({ title: 'Refresh failed', description: getApiError(err, 'Could not refresh master data'), variant: 'destructive' });
    } finally {
      setHealing(false);
    }
  }, [healing, session.id, session.status, children, load, toast]);

  const cellarSession = children.find(c => c.location_type === 'cellar');
  const locationSessions = children.filter(c => c.location_type !== 'cellar');
  const totalLocations = children.length;
  const completedCount = children.filter(c => c.status === 'completed' || c.status === 'locked').length;
  const allComplete = totalLocations > 0 && completedCount === totalLocations;
  const postedTransfers = transfers.filter(t => t.status === 'posted').length;
  const draftTransfers = transfers.filter(t => t.status === 'draft').length;

  if (loading) {
    return <Card><CardContent className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading...</CardContent></Card>;
  }

  // iter409 — Zombie session rescue. If the parent is in multi-location
  // mode but has zero child sessions (created before the create-flow
  // fix), let the auditor pick locations + re-run enable_multi_location.
  if (totalLocations === 0) {
    return (
      <ZombieRescuePanel session={session} auditors={auditors} onRescued={load} />
    );
  }

  return (
    <div className="space-y-4" data-testid="parent-audit-dashboard">
      {/* iter422 — Tabbed parent dashboard. Overview keeps the legacy
          per-location cards + transfers list, Calculate & Results is
          the single source of truth for results (Consolidated /
          per-location / subset rollups). Both online and offline. */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md" data-testid="parent-dashboard-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Warehouse className="h-3.5 w-3.5 mr-1.5" />Overview
          </TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-calculate-results">
            <Calculator className="h-3.5 w-3.5 mr-1.5" />Calculate &amp; Results
            {!reviewData && reviewLoading && <Loader2 className="h-3 w-3 ml-1.5 animate-spin" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
      {/* Overview */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Warehouse className="h-5 w-5 text-indigo-600" />Multi-Location Audit Overview
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{session.audit_mode === 'multi_with_cellar' ? 'With Cellar' : 'Without Cellar'}</Badge>
              {/* iter382 — Refresh from Master for the whole audit. Heals
                  parent + every editable child in one click: count
                  snapshots, stale unit_labels, missing entry costs,
                  AND all transfer (cellar→bar) costs. */}
              {session.status !== 'locked' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleHealAll}
                  disabled={healing}
                  data-testid="parent-refresh-master-btn"
                  title="Refresh stale names / unit dropdowns / missing costs / transfer costs across this audit"
                >
                  {healing ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  Refresh from Master
                </Button>
              )}
              {/* iter424 — Removed duplicate "Calculate & Results"
                  shortcut. The tab strip above is the canonical entry
                  point; an extra button was redundant. */}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-indigo-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Locations</p>
              <p className="text-xl font-bold text-indigo-700" data-testid="location-count">{totalLocations}</p>
            </div>
            <div className={`rounded-lg p-3 ${allComplete ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className={`text-xl font-bold ${allComplete ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="completed-count">{completedCount}/{totalLocations}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Transfers</p>
              <p className="text-xl font-bold text-slate-700" data-testid="transfer-count">
                {postedTransfers}{draftTransfers > 0 && <span className="text-amber-500 text-sm ml-1">+{draftTransfers} draft</span>}
              </p>
            </div>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500 rounded-full" style={{ width: `${totalLocations > 0 ? (completedCount / totalLocations) * 100 : 0}%` }} />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{allComplete ? 'All locations complete \u2014 ready for consolidated review' : `${totalLocations - completedCount} location(s) still in progress`}</p>
        </CardContent>
      </Card>

      {/* Cellar */}
      {cellarSession && (
        <Card className="border-purple-200" data-testid="cellar-session-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Package className="h-4 w-4 text-purple-600" />Cellar / Mainstock</CardTitle>
            <CardDescription className="text-xs">Central receiving and storage location</CardDescription>
          </CardHeader>
          <CardContent>
            <LocationCard child={cellarSession} auditors={auditors} isCellar onOpen={() => navigate(`/stocktaking/sessions/${cellarSession.id}`)} />
          </CardContent>
        </Card>
      )}

      {/* Location Sessions */}
      <Card data-testid="location-sessions-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" />Outlet Locations ({locationSessions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {locationSessions.map(child => (
              <LocationCard key={child.id} child={child} auditors={auditors} onOpen={() => navigate(`/stocktaking/sessions/${child.id}`)} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Internal Transfers (read-only overview — managed in location wizards) */}
      <Card data-testid="transfers-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-teal-600" />Internal Stock Transfers ({transfers.length})
          </CardTitle>
          <CardDescription className="text-xs">Transfers are managed within each location&apos;s wizard. This is a summary view.</CardDescription>
        </CardHeader>
        <CardContent>
          {transfers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No internal transfers yet. Open a location to issue or receive stock.</p>
          ) : (
            <div className="space-y-1.5">
              {transfers.map(t => (
                <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded-lg border text-xs" data-testid={`transfer-row-${t.id}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge variant="outline" className={`shrink-0 ${t.status === 'posted' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{t.status}</Badge>
                    <span className="font-medium truncate">{t.from_location_name || 'Supplier'}</span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-medium truncate">{t.to_location_name || '?'}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-2">
                    <span className="text-muted-foreground">{t.transfer_date}</span>
                    <span className="font-medium tabular-nums">{fmtCurrency(t.total_value)}</span>
                    <span className="text-muted-foreground">{t.line_count} item{t.line_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consolidated Review Panel */}
      {/* iter422 — Removed inline Consolidated Review panel from the
          Overview tab; the full panel lives under Calculate & Results
          below. */}
        </TabsContent>

        <TabsContent value="results" className="space-y-4 mt-4">
          {reviewLoading && (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading results…
            </CardContent></Card>
          )}
          {!reviewLoading && !reviewData && (
            <Card><CardContent className="py-8 text-center">
              <p className="text-xs text-muted-foreground mb-3">No results available yet. Complete and calculate each location to see consolidated results here.</p>
              <Button size="sm" variant="outline" onClick={loadReview} data-testid="results-load-btn">
                <RefreshCw className="h-3 w-3 mr-1" />Refresh
              </Button>
            </CardContent></Card>
          )}
          {!reviewLoading && reviewData && (
            <MultiLocationCalculateResultsPanel
              data={reviewData}
              parentSession={session}
              onReload={cascadeRecalcAndReload}
              reloading={reloadCascading}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Location Card ──────────────────────────────────

function LocationCard({ child, auditors, isCellar, onOpen }) {
  const statusCls = STATUS_COLORS[child.status] || STATUS_COLORS.draft;
  const auditor = auditors.find(a => (a.user_id || a.id) === child.auditor_id);
  const auditorName = auditor ? `${auditor.first_name} ${auditor.last_name}` : 'Unassigned';
  // Release Contract S2 — surface "no closing counts" so the auditor knows
  // why this location's Calculate button is disabled without opening it.
  const noCounts = (child.counted_lines ?? 1) === 0 && !['completed', 'locked'].includes(child.status);
  return (
    <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border cursor-pointer hover:bg-slate-50/50 transition-colors ${isCellar ? 'border-purple-200 bg-purple-50/30' : ''}`} onClick={onOpen} data-testid={`location-session-${child.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCellar ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
          {isCellar ? <Package className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
        </div>
        <div>
          <p className="text-sm font-medium">{child.location_name}</p>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-0.5"><Users className="h-3 w-3" />{auditorName}</span>
            <span className="font-mono">{child.session_reference}</span>
          </div>
          {noCounts && (
            <p className="text-[10px] text-amber-600 mt-0.5" data-testid={`location-no-counts-${child.id}`}>
              No closing counts yet
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={`text-[10px] ${statusCls}`}>{child.status?.replace('_', ' ')}</Badge>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}


// ── Consolidated Review Panel — Full Report ──────────────────────
// iter420l — Reusable clickable KPI tile with a formula popover. Used
// across the consolidated review so the operator can audit how each
// number was derived without leaving the screen. Formulas mirror those
// already documented on the single-site Calculate panel — no new math
// is introduced here.
function ConsolidatedKpiTile({ label, value, valueClassName = '', subText = null, formula, dataTestId, onDrillDown }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="bg-indigo-50/50 rounded-lg p-3 text-left w-full hover:ring-2 hover:ring-indigo-200 transition focus:outline-none focus:ring-2 focus:ring-indigo-300"
          data-testid={dataTestId}
        >
          <p className="text-[10px] text-muted-foreground">{label}</p>
          <p className={`text-lg font-bold tabular-nums ${valueClassName}`}>{value}</p>
          {subText && <p className="text-[10px] text-muted-foreground">{subText}</p>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-80 text-xs">
        <p className="font-semibold text-slate-800 mb-1">{label}</p>
        <p className="font-mono text-[11px] text-slate-700 whitespace-pre-line">{formula}</p>
        {/* iter439c — surface a drill-down entry point on tiles that
            have a consolidated breakdown wired up. Kept inside the
            popover so the click flow is two-step (open formula → open
            drill-down) and the tile itself remains a pure formula tip
            on tiles that don't have a drill-down yet. */}
        {onDrillDown && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDrillDown(); }}
            className="mt-2 w-full text-[11px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline text-left"
            data-testid={`${dataTestId}-drilldown-btn`}
          >
            Open product-level drill-down →
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ConsolidatedReviewPanel({ data, onClose }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  // iter439c — drill-down dialog state. Tiles call open() with a tile
  // key + label; ConsolidatedBreakdownDialog fetches the per-product
  // payload aggregated across every child.
  const [drillTile, setDrillTile] = useState(null);
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const { locations, consolidated, completion, transfer_summary, audit_mode, cellar, outlet_aggregate, parent_reference, site_name, session_date, period_from, period_to, parent_session_id } = data;
  const hasCellar = audit_mode === 'multi_with_cellar';
  const resultColor = (v) => v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-slate-600';

  return (
    <div className="space-y-4" data-testid="consolidated-review-panel">
      {/* iter439c — drill-down dialog mounted at panel root so any
          tile in this tree can open it via setDrillTile(). */}
      <ConsolidatedBreakdownDialog
        open={!!drillTile}
        parentSessionId={parent_session_id}
        tile={drillTile?.tile || 'estimated_receipts'}
        tileLabel={drillTile?.label}
        onClose={() => setDrillTile(null)}
      />
      {/* Report Header */}
      <Card className="bg-gradient-to-br from-indigo-50/80 to-slate-50/80 border-indigo-200">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Multi-Location Stock Audit Report</h2>
              <p className="text-sm text-slate-600 mt-0.5">{site_name} — {parent_reference}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                {session_date && <span>Audit Date: {session_date}</span>}
                {period_from && period_to && <span>Period: {period_from} to {period_to}</span>}
                <Badge variant="outline" className="text-[10px]">{hasCellar ? 'With Cellar' : 'Without Cellar'}</Badge>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={onClose} className="text-xs">Close</Button>
          </div>
        </CardContent>
      </Card>

      {/* Completion Status */}
      <Card data-testid="completion-status">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Completion Status</p>
            {completion.all_calculated
              ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]"><CheckCircle className="h-3 w-3 mr-0.5" />All Calculated</Badge>
              : <Badge className="bg-amber-100 text-amber-800 text-[10px]"><AlertCircle className="h-3 w-3 mr-0.5" />{completion.calculated}/{completion.total_locations} Calculated</Badge>
            }
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {locations.map(loc => (
              <div key={loc.session_id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${loc.has_calculation ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                {loc.location_type === 'cellar' ? <Package className="h-3.5 w-3.5 text-purple-600 shrink-0" /> : <MapPin className="h-3.5 w-3.5 text-blue-600 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{loc.location_name}</p>
                  <p className="text-[10px] text-muted-foreground">{loc.auditor_name}</p>
                </div>
                {loc.has_calculation ? <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0" /> : <Clock className="h-3 w-3 text-amber-500 shrink-0" />}
              </div>
            ))}
          </div>
          {!completion.all_calculated && (
            <p className="text-[10px] text-amber-600 mt-2">Consolidated totals based on {completion.calculated} of {completion.total_locations} locations. Uncalculated locations excluded.</p>
          )}
        </CardContent>
      </Card>

      {/* Consolidated Totals */}
      <Card className="border-indigo-200" data-testid="consolidated-totals">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            Consolidated Stock Audit Result
            {(() => {
              const methodology = consolidated.headline_methodology || 'legacy';
              const isRevised = methodology === 'revised';
              return (
                <Badge
                  variant="outline"
                  className={`text-[10px] font-medium ${isRevised ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-50 text-slate-600'}`}
                  data-testid="consolidated-methodology-badge"
                >
                  {isRevised ? 'Actual Receipts − Est. Receipts + Allow − Mgmt' : 'Legacy — PLU-led'}
                </Badge>
              );
            })()}
            {consolidated.headline_fallback_reason && (
              <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700" title={`Methodology not applied: ${String(consolidated.headline_fallback_reason).replace(/_/g, ' ')}`} data-testid="consolidated-fallback-badge">
                Fallback
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-xs">Inter-location transfers eliminated. {hasCellar && 'Deliveries/credits from cellar; sales from outlets.'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] text-muted-foreground">Opening Stock</p><p className="text-sm font-bold tabular-nums">{fmtCurrency(consolidated.opening_stock_value)}</p></div>
            <div className="bg-emerald-50/80 rounded-lg p-2.5"><p className="text-[10px] text-emerald-700">+ Deliveries</p><p className="text-sm font-bold tabular-nums text-emerald-700">{fmtCurrency(consolidated.deliveries_value)}</p></div>
            <div className="bg-red-50/60 rounded-lg p-2.5"><p className="text-[10px] text-red-600">- Credits</p><p className="text-sm font-bold tabular-nums text-red-600">{fmtCurrency(consolidated.credits_value)}</p></div>
            <div className="bg-orange-50/60 rounded-lg p-2.5"><p className="text-[10px] text-orange-600">- {hasCellar ? 'Sales (Outlets)' : 'Cost of Sales'}</p><p className="text-sm font-bold tabular-nums text-orange-600">{fmtCurrency(consolidated.sales_at_cost)}</p></div>
          </div>
          {(() => {
            const methodology = consolidated.headline_methodology || 'legacy';
            const isRevised = methodology === 'revised';
            const headlineResult = consolidated.headline_stocktake_result
              ?? consolidated.stocktake_result_legacy_adjusted
              ?? consolidated.stocktake_result;
            if (isRevised && (consolidated.accounts_sales_incl_vat != null || consolidated.accounts_sales_excl_vat != null)) {
              // Revised headline — Actual Receipts − Estimated Receipts (incl VAT, iter329)
              const accSales = consolidated.accounts_sales_incl_vat ?? consolidated.accounts_sales_excl_vat;
              const estRec = consolidated.estimated_receipts_incl_vat ?? consolidated.estimated_receipts_excl_vat;
              return (
                <>
                  <div className="grid grid-cols-3 gap-3 pt-3 border-t border-indigo-200">
                    <ConsolidatedKpiTile
                      label="Actual Receipts (incl VAT)"
                      value={fmtCurrency(accSales)}
                      formula={`Σ(actual_receipts_incl_vat) across every non-cellar location\n\nRolled up from each location's Sales step (incl VAT till receipts).`}
                      dataTestId="consolidated-kpi-actual-receipts"
                    />
                    <ConsolidatedKpiTile
                      label="- Estimated Receipts (incl VAT)"
                      value={fmtCurrency(estRec)}
                      formula={`Estimated Receipts = (Expected Closing − Actual Closing) × Selling-Price Ratio\n\nRolled up from each non-cellar location's Calculate step.`}
                      dataTestId="consolidated-kpi-estimated-receipts"
                      onDrillDown={() => setDrillTile({ tile: 'estimated_receipts', label: 'Estimated Receipts' })}
                    />
                    <ConsolidatedKpiTile
                      label="= Result"
                      value={fmtCurrency(headlineResult)}
                      valueClassName={resultColor(headlineResult)}
                      subText={`${(headlineResult ?? 0) >= 0 ? 'Surplus' : 'Deficit'} | ${consolidated.line_count} lines`}
                      formula={`Raw Result = Actual Receipts (incl VAT) − Estimated Receipts (incl VAT).\nFinal Result = Raw Result + Allowances − Management Retail (inc VAT).\n\nSurplus when actual takings exceed what stock movement predicts;\nDeficit when actual takings fall short.\n\nMatches the per-location Result formula on each child tab.`}
                      dataTestId="consolidated-kpi-result"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3 pt-2 text-[10px] text-muted-foreground">
                    <span>Expected Closing (ref): <span className="tabular-nums">{fmtCurrency(consolidated.expected_closing_value)}</span></span>
                    <span>Actual Closing (ref): <span className="tabular-nums">{fmtCurrency(consolidated.total_value)}</span></span>
                    <span>Legacy result (ref): <span className="tabular-nums">{fmtCurrency(consolidated.stocktake_result_legacy ?? consolidated.stocktake_result)}</span></span>
                  </div>
                  {consolidated.cellar_physical_variance_excluded_from_headline && (
                    <div className="mt-2 flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50/60 px-3 py-2 text-xs" data-testid="cellar-physical-variance-excluded">
                      <span className="text-purple-700">Cellar physical variance (at cost) — excluded from this headline</span>
                      <span className={`font-semibold tabular-nums ${resultColor(consolidated.cellar_physical_variance_at_cost)}`}>{fmtCurrency(consolidated.cellar_physical_variance_at_cost)}</span>
                    </div>
                  )}
                </>
              );
            }
            return (
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-indigo-200">
                <ConsolidatedKpiTile
                  label="Expected Closing"
                  value={fmtCurrency(consolidated.expected_closing_value)}
                  formula={`Expected Closing = Σ(Opening + Deliveries − Credits − Sales − Adjustments) across all locations\n\nInter-location transfers net to zero (cellar issues = outlet receives).`}
                  dataTestId="consolidated-kpi-expected-closing"
                />
                <ConsolidatedKpiTile
                  label="Actual Closing"
                  value={fmtCurrency(consolidated.total_value)}
                  formula={`Actual Closing = Σ(closing stock value) across all locations\n\nIncludes cellar stock + every outlet's on-hand stock at period end.`}
                  dataTestId="consolidated-kpi-actual-closing"
                />
                <ConsolidatedKpiTile
                  label="Result"
                  value={fmtCurrency(headlineResult)}
                  valueClassName={resultColor(headlineResult)}
                  subText={`${(headlineResult ?? 0) >= 0 ? 'Surplus' : 'Deficit'} | ${consolidated.line_count} lines`}
                  formula={`Stock Result at Cost = Actual Closing − Expected Closing = Σ product variance + Unallocated variance.\nLegacy headline = Cost result − Management Retail (inc VAT) + Included Wastage (allowances apply only to the receipts-based result).\n\nMatches the per-location Result formula on each child tab so parent total = Σ(child totals).`}
                  dataTestId="consolidated-kpi-result"
                />
              </div>
            );
          })()}
          {(consolidated.headline_gross_profit != null || consolidated.headline_gp_pct != null) && (
            <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-indigo-100 text-xs">
              <div><span className="text-muted-foreground">Gross Profit:</span>{' '}<span className="font-semibold tabular-nums">{fmtCurrency(consolidated.headline_gross_profit)}</span></div>
              <div><span className="text-muted-foreground">GP %:</span>{' '}<span className="font-semibold tabular-nums">{consolidated.headline_gp_pct != null ? `${consolidated.headline_gp_pct}%` : '—'}</span></div>
            </div>
          )}

          {/* 2026-04-24 iter313 — Adjustments split (Management Cost of Sales + Retail inc VAT sub-row + Allowance)
              2026-05-07 iter333 — Management headline flipped to INC VAT. */}
          {(() => {
            const mgmtB = consolidated.management_value || 0;
            const mgmtIncl = consolidated.management_sales_incl_vat || 0;
            const allow = consolidated.allowance_value || 0;
            if (!mgmtIncl && !allow) return null;
            return (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/40 p-3 space-y-1.5" data-testid="consolidated-adjustments-split">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Adjustments split (rolled up across locations)</p>
                {mgmtIncl !== 0 && (
                  <>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-700">Management retail (inc VAT — summed across locations)</span>
                      <span className="font-semibold tabular-nums">{fmtCurrency(mgmtIncl)}</span>
                    </div>
                    <div className="flex justify-between items-center text-[11px] text-muted-foreground pl-4">
                      <span>+/- Management Cost of Sales (part of COS)</span>
                      <span className="tabular-nums">{fmtCurrency(mgmtB)}</span>
                    </div>
                  </>
                )}
                {allow !== 0 && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-700">Allowances (excluded from COS, applied to final result)</span>
                    <span className="font-semibold tabular-nums">{fmtCurrency(allow)}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* 2026-04-24 iter313 — Raw → −Allowances → −Mgmt Retail (inc VAT) → = Final adjusted result block
              2026-05-07 iter333 — Mgmt subtraction now INC VAT. */}
          {(() => {
            const methodology = consolidated.headline_methodology || 'legacy';
            const isRevised = methodology === 'revised';
            const rawRevised = consolidated.stocktake_result_revised;
            const rawLegacy = consolidated.stocktake_result_legacy ?? consolidated.stocktake_result;
            const headlineRaw = isRevised ? rawRevised : rawLegacy;
            const adjustedRevised = consolidated.stocktake_result_revised_adjusted;
            const adjustedLegacy = consolidated.stocktake_result_legacy_adjusted ?? rawLegacy;
            const finalResult = isRevised
              ? (adjustedRevised ?? consolidated.headline_stocktake_result ?? rawRevised)
              : adjustedLegacy;
            const allow = consolidated.allowance_value || 0;
            const mgmtIncl = consolidated.management_sales_incl_vat || 0;
            const hasAllow = isRevised && allow !== 0 && headlineRaw != null;
            const hasMgmt = mgmtIncl !== 0 && headlineRaw != null;
            if (!hasAllow && !hasMgmt) return null;
            const cols = 2 + (hasAllow ? 1 : 0) + (hasMgmt ? 1 : 0);
            const gridCls = cols === 4 ? 'grid-cols-4' : 'grid-cols-3';
            return (
              <div className={`mt-3 grid ${gridCls} gap-3 text-xs`} data-testid="consolidated-allowance-adjustment-block">
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  <span className="text-slate-500">Raw {isRevised ? 'Revised' : 'Legacy'} Result</span>
                  <span className="font-semibold tabular-nums text-slate-800 block" data-testid="consolidated-headline-raw-result">
                    {fmtCurrency(headlineRaw)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">before adjustments</span>
                </div>
                {hasAllow && (
                  <div className="p-2 bg-indigo-50 rounded border border-indigo-200">
                    <span className="text-indigo-600">− Allowances (direct adj.)</span>
                    <span className="font-semibold tabular-nums text-indigo-700 block" data-testid="consolidated-headline-allowance-adjustment">
                      {fmtCurrency(-allow)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">excluded from COS / GP</span>
                  </div>
                )}
                {hasMgmt && (
                  <div className="p-2 bg-teal-50 rounded border border-teal-200">
                    <span className="text-teal-700">+ Mgmt Retail (inc VAT)</span>
                    <span className="font-semibold tabular-nums text-teal-800 block" data-testid="consolidated-headline-management-retail-adjustment">
                      {fmtCurrency(mgmtIncl)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">retail diverted to Mgmt</span>
                  </div>
                )}
                <div className={`p-2 rounded border ${(finalResult ?? 0) > 0 ? 'bg-emerald-50 border-emerald-200' : (finalResult ?? 0) < 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                  <span className="text-slate-500">= Final Result</span>
                  <span className={`font-semibold tabular-nums block ${(finalResult ?? 0) > 0 ? 'text-emerald-700' : (finalResult ?? 0) < 0 ? 'text-red-700' : 'text-slate-800'}`} data-testid="consolidated-headline-final-result">
                    {fmtCurrency(finalResult)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">after adjustments</span>
                </div>
              </div>
            );
          })()}

          {/* 2026-05-07 iter334 — Consolidated Yield % denominator ADDS Management (inc VAT) */}
          {(() => {
            // Canonical Yield (same as single location): Actual ÷ (Gross Estimated Receipts + Management).
            const estR = Number(consolidated.estimated_receipts_incl_vat_gross
              ?? ((consolidated.estimated_receipts_incl_vat ?? 0) + (consolidated.allowance_value || 0)));
            const actR = Number(consolidated.accounts_sales_incl_vat ?? 0);
            const mgmt = Number(consolidated.management_sales_incl_vat ?? 0);
            const denom = estR + mgmt;
            if (denom <= 0 || consolidated.yield_pct == null) return null;
            const yp = Number(consolidated.yield_pct);
            const band = Math.abs(yp - 100);
            const color = band <= 2 ? 'text-emerald-600' : band <= 10 ? 'text-amber-600' : 'text-red-600';
            return (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between" data-testid="consolidated-yield-kpi">
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Yield %</p>
                  <p className={`text-lg font-bold ${color} tabular-nums`}>{yp.toFixed(1)}%</p>
                  <p className="text-[11px] text-muted-foreground">Actual Receipts / (Gross Estimated Receipts + Management) (incl VAT) × 100</p>
                </div>
                <div className="text-right text-[11px] text-muted-foreground">
                  <p>Actual Receipts: <span className="tabular-nums">{fmtCurrency(actR)}</span></p>
                  <p>Gross Estimated Receipts: <span className="tabular-nums">{fmtCurrency(estR)}</span></p>
                  {mgmt !== 0 && (
                    <p>+ Management (inc VAT): <span className="tabular-nums">{fmtCurrency(mgmt)}</span></p>
                  )}
                  <p className="text-slate-700">= Target: <span className="tabular-nums">{fmtCurrency(denom)}</span></p>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Cellar Results */}
      {hasCellar && cellar?.calculation && (
        <Card className="border-purple-200" data-testid="cellar-report-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Package className="h-4 w-4 text-purple-600" />{cellar.location_name} — Cellar Stock Movement</CardTitle>
            <CardDescription className="text-xs">Receives supplier deliveries, issues stock to outlets. {cellar.auditor_name} | {cellar.session_reference}</CardDescription>
          </CardHeader>
          <CardContent>
            <LocationCalcGrid calc={cellar.calculation} locationType="cellar" />
            <div className="flex justify-end mt-2">
              <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => navigate(`/stocktaking/sessions/${cellar.session_id}`)}><Eye className="h-3 w-3" />View Cellar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outlet Aggregate */}
      {hasCellar && outlet_aggregate && (
        <Card className="border-blue-200" data-testid="outlet-aggregate-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" />Outlet Trading Results ({outlet_aggregate.location_count} locations)</CardTitle>
            <CardDescription className="text-xs">Stock from cellar, sales, returns. Excludes cellar.</CardDescription>
          </CardHeader>
          <CardContent>
            <LocationCalcGrid calc={outlet_aggregate} locationType="outlet" />
          </CardContent>
        </Card>
      )}

      {/* Transfer Reconciliation */}
      {transfer_summary.total_count > 0 && (
        <Card data-testid="transfer-reconciliation">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-teal-600" />Internal Transfer Reconciliation</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4 text-xs mb-2">
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">{transfer_summary.posted_count} posted ({fmtCurrency(transfer_summary.posted_value)})</span>
              {transfer_summary.draft_count > 0 && <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">{transfer_summary.draft_count} draft — excluded</span>}
            </div>
            <div className="space-y-1">
              {(transfer_summary.details || []).map(t => (
                <div key={t.id} className="flex items-center justify-between px-2.5 py-1.5 rounded border text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className={`shrink-0 text-[9px] ${t.status === 'posted' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{t.status}</Badge>
                    <span className="font-medium truncate">{t.from_location}</span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-medium truncate">{t.to_location}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-2 text-muted-foreground">
                    <span>{t.transfer_date}</span>
                    <span className="font-medium text-slate-700 tabular-nums">{fmtCurrency(t.total_value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Location Detail */}
      <Card data-testid="per-location-detail">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Per-Location Detail</CardTitle>
          <CardDescription className="text-xs">Click to expand location calculation.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {locations.map(loc => {
              const isOpen = expanded[loc.session_id];
              const c = loc.calculation;
              const isCellar = loc.location_type === 'cellar';
              return (
                <div key={loc.session_id} className={`border rounded-lg overflow-hidden ${isCellar ? 'border-purple-200' : ''}`} data-testid={`location-review-${loc.session_id}`}>
                  <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50/50" onClick={() => toggleExpand(loc.session_id)}>
                    <div className="flex items-center gap-2">
                      {isCellar ? <Package className="h-3.5 w-3.5 text-purple-600" /> : <MapPin className="h-3.5 w-3.5 text-blue-600" />}
                      <span className="text-sm font-medium">{loc.location_name}</span>
                      <Badge className={`text-[9px] ${STATUS_COLORS[loc.status] || ''}`}>{loc.status?.replace('_', ' ')}</Badge>
                      <span className="text-[10px] text-muted-foreground">{loc.auditor_name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {c && <span className={`font-medium tabular-nums ${resultColor(c.stocktake_result)}`}>{fmtCurrency(c.stocktake_result)}</span>}
                      {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                  </div>
                  {isOpen && c && (
                    <div className="px-3 pb-3 border-t bg-slate-50/30">
                      <LocationCalcGrid calc={c} locationType={loc.location_type} />
                      <div className="flex items-center justify-between mt-2 pt-1 border-t">
                        <p className="text-[10px] text-muted-foreground">v{c.version} | {c.line_count} lines | {c.calculated_at ? new Date(c.calculated_at).toLocaleString('en-GB') : ''}</p>
                        <Button size="sm" variant="ghost" className="text-[10px] gap-1 h-6" onClick={() => navigate(`/stocktaking/sessions/${loc.session_id}`)}><Eye className="h-3 w-3" />Open</Button>
                      </div>
                    </div>
                  )}
                  {isOpen && !c && (
                    <div className="px-3 pb-3 border-t">
                      <p className="text-xs text-amber-600 py-2">No calculation yet.</p>
                      <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => navigate(`/stocktaking/sessions/${loc.session_id}`)}>Open Location</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Multi-Location Calculate & Results Panel ─────────────────────────
// iter422 — Single source of truth for multi-location stocktake results.
// Replaces the standalone Consolidated Review pop-out. A top toggle row
// lets the auditor view:
//   • Consolidated (every location rolled up — Σ across all)
//   • A subset of locations (multi-select; Σ across selected only)
//   • A single location (its own KPIs, drilled in)
// "Consolidated" is exclusive: selecting it clears any individual
// location pills, and picking any location clears Consolidated. The
// math is the same as the backend `routes/multi_location.py`
// (legacy + revised methodology, allowance/management split, GP%) —
// just sliced over a different set of children.
function MultiLocationCalculateResultsPanel({ data, parentSession, onReload, reloading = false }) {
  const navigate = useNavigate();
  const { locations = [], completion = {}, audit_mode, transfer_summary = {}, _offline } = data;
  const hasCellar = audit_mode === 'multi_with_cellar';

  // Selection state ──────────────────────────────────────────────
  // 'consolidated' is the default. The pill row below toggles between
  // 'consolidated' and a set of individual location_ids.
  const [mode, setMode] = useState('consolidated');     // 'consolidated' | 'subset'
  const [selectedIds, setSelectedIds] = useState([]);   // session_id[]

  // iter423 — Breakdown dialog state. When a tile is clicked, this
  // holds the kpi key; the dialog builds its payload on-demand using
  // `buildMultiLocBreakdown`.
  const [breakdownKey, setBreakdownKey] = useState(null);

  // iter425 — Per-product rollup across all children for the
  // by-product drill-down tab in the breakdown dialog. Fetched once
  // on mount (or refresh) since it's an expensive aggregation; cached
  // here for instant tab switches.
  const [productRollup, setProductRollup] = useState(null);
  const [productRollupLoading, setProductRollupLoading] = useState(false);
  // iter440 — Sales-side per-product breakdown (Estimated Receipts).
  // Pre-fetched alongside productRollup so the Estimated Receipts
  // drill-down shows category/subcategory groups + location filter
  // chips instantly.
  const [salesBreakdown, setSalesBreakdown] = useState(null);
  useEffect(() => {
    if (!data?.parent_session_id || _offline) return;
    let cancelled = false;
    setProductRollupLoading(true);
    multiLocationService.getProductsRollup(data.parent_session_id)
      .then(res => { if (!cancelled) setProductRollup(res); })
      .catch(() => { /* silent — breakdown dialog falls back to by-location only */ })
      .finally(() => { if (!cancelled) setProductRollupLoading(false); });
    multiLocationService.getConsolidatedBreakdown(data.parent_session_id, 'estimated_receipts')
      .then(res => { if (!cancelled) setSalesBreakdown(res); })
      .catch(() => { /* silent — sales-side drill-down falls back to empty */ });
    return () => { cancelled = true; };
  }, [data?.parent_session_id, _offline]);

  const setConsolidated = () => { setMode('consolidated'); setSelectedIds([]); };
  const toggleLocation = (sid) => {
    setMode('subset');
    setSelectedIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  // Derive the visible set of locations.
  const visibleLocations = useMemo(() => {
    if (mode === 'consolidated') return locations.filter(l => l.has_calculation);
    return locations.filter(l => selectedIds.includes(l.session_id) && l.has_calculation);
  }, [mode, selectedIds, locations]);

  // Rollup. For 'consolidated' we use the backend-provided totals
  // verbatim so the source-of-truth is the server when online.
  // For 'subset' (or offline), we recompute using the same formulas
  // as `aggregateLocationCalcs` (which mirrors the backend).
  const rollup = useMemo(() => {
    if (mode === 'consolidated' && data.consolidated && !_offline) {
      return data.consolidated;
    }
    return aggregateLocationCalcs(visibleLocations.map(l => l.calculation));
  }, [mode, visibleLocations, data.consolidated, _offline]);

  const singleLocation = mode === 'subset' && selectedIds.length === 1
    ? visibleLocations[0]
    : null;

  const resultColor = (v) => v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-slate-600';

  // Headline result picker — same logic as ConsolidatedReviewPanel
  const isRevised = (rollup.headline_methodology || 'legacy') === 'revised';
  // Configured headline (org setting), same fallback as the single-location panel.
  const headlineResult = rollup.headline_stocktake_result
    ?? rollup.stocktake_result_legacy_adjusted
    ?? rollup.stocktake_result;

  const selectionLabel = mode === 'consolidated'
    ? 'Consolidated (all locations)'
    : singleLocation
      ? singleLocation.location_name
      : `${selectedIds.length} location${selectedIds.length !== 1 ? 's' : ''} selected`;

  return (
    <div className="space-y-4" data-testid="multi-location-results-panel">
      {/* Header */}
      <Card className="bg-gradient-to-br from-indigo-50/80 to-slate-50/80 border-indigo-200">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Stock Audit Results</h2>
              <p className="text-sm text-slate-600 mt-0.5">{data.site_name} — {data.parent_reference}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                {data.session_date && <span>Audit Date: {data.session_date}</span>}
                {data.period_from && data.period_to && <span>Period: {data.period_from} to {data.period_to}</span>}
                <Badge variant="outline" className="text-[10px]">{hasCellar ? 'With Cellar' : 'Without Cellar'}</Badge>
                {_offline && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">Offline view</Badge>}
              </div>
            </div>
            {onReload && (
              <Button size="sm" variant="ghost" onClick={onReload} disabled={reloading} className="text-xs gap-1" data-testid="results-refresh">
                <RefreshCw className={`h-3 w-3 ${reloading ? 'animate-spin' : ''}`} />
                {reloading ? 'Recalculating…' : 'Refresh'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Toggle row — pill selector */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">View</p>
              <p className="text-[11px] text-muted-foreground">{selectionLabel}</p>
            </div>
            <Badge
              variant="outline"
              className={`text-[10px] ${completion.all_calculated ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-amber-300 bg-amber-50 text-amber-700'}`}
              data-testid="results-completion-badge"
            >
              {completion.all_calculated
                ? <><CheckCircle className="h-3 w-3 mr-0.5 inline" />All {completion.total_locations} calculated</>
                : <><AlertCircle className="h-3 w-3 mr-0.5 inline" />{completion.calculated}/{completion.total_locations} calculated</>
              }
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="results-location-toggle">
            <button
              type="button"
              onClick={setConsolidated}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                mode === 'consolidated'
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
              }`}
              data-testid="results-toggle-consolidated"
            >
              <Layers className="h-3 w-3 mr-1.5 inline" />Consolidated
            </button>
            <span className="text-slate-300 self-center text-xs px-1">|</span>
            {locations.map(loc => {
              const active = mode === 'subset' && selectedIds.includes(loc.session_id);
              const disabled = !loc.has_calculation;
              const isCellar = loc.location_type === 'cellar';
              return (
                <button
                  key={loc.session_id}
                  type="button"
                  onClick={() => !disabled && toggleLocation(loc.session_id)}
                  disabled={disabled}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    active
                      ? isCellar
                        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                        : 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : disabled
                        ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                  }`}
                  data-testid={`results-toggle-location-${loc.session_id}`}
                  title={disabled ? 'No calculation yet — open this location and run Calculate to include it' : undefined}
                >
                  {isCellar ? <Package className="h-3 w-3 mr-1.5 inline" /> : <MapPin className="h-3 w-3 mr-1.5 inline" />}
                  {loc.location_name}
                  {disabled && <span className="ml-1 text-[9px]">(pending)</span>}
                </button>
              );
            })}
          </div>
          {mode === 'subset' && selectedIds.length === 0 && (
            <p className="text-[11px] text-amber-600 mt-2">Pick at least one location, or switch back to Consolidated.</p>
          )}
        </CardContent>
      </Card>

      {/* Empty selection — guard */}
      {mode === 'subset' && selectedIds.length === 0 && (
        <Card><CardContent className="py-6 text-center text-xs text-muted-foreground">
          Select one or more locations above to see their results.
        </CardContent></Card>
      )}

      {/* iter423-fix-v2 — Results Breakdown matching the single-loc panel.
          Part 1: Stock Movement (At Cost) → Expected vs Actual Closing.
          Part 2: Sales Reconciliation (Inc VAT) → Stocktake Result.
          Same look-and-feel as `StockAuditResultsPanel` so users see the
          exact same arithmetic story across single-loc and multi-loc. */}
      {(mode === 'consolidated' || selectedIds.length > 0) && (
        <Card data-testid="results-breakdown-parts">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Results Breakdown</CardTitle>
            <CardDescription className="text-xs">Part 1 is at cost, Part 2 is Inc VAT. Click any figure for its per-location breakdown.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-primary border-b pb-1">Part 1 · Stock Movement (At Cost)</h4>
              <BreakdownRow label="Opening Stock" value={rollup.opening_stock_value} onClick={() => setBreakdownKey('opening')} testId="rb-opening" />
              <BreakdownRow label="+ Deliveries" tone="emerald" value={rollup.deliveries_value} onClick={() => setBreakdownKey('deliveries')} testId="rb-deliveries" />
              <BreakdownRow label="− Credits" tone="red" value={-(rollup.credits_value || 0)} onClick={() => setBreakdownKey('credits')} testId="rb-credits" />
              {hasCellar && (
                <>
                  <BreakdownRow label="+ Transfers In" tone="emerald" value={rollup.transfers_in_total} onClick={() => setBreakdownKey('transfers_in')} testId="rb-transfers-in" />
                  <BreakdownRow label="− Transfers Out" tone="orange" value={-(rollup.transfers_out_total || 0)} onClick={() => setBreakdownKey('transfers_out')} testId="rb-transfers-out" />
                </>
              )}
              <BreakdownRow
                label={isRevised && rollup.cost_of_sales_indirect != null ? '− Consumption at Cost' : '− Cost of Sales (PLU)'}
                tone="orange"
                value={-(isRevised && rollup.cost_of_sales_indirect != null ? rollup.cost_of_sales_indirect : rollup.sales_at_cost || 0)}
                onClick={() => setBreakdownKey(isRevised && rollup.cost_of_sales_indirect != null ? 'cos_indirect' : 'sales_at_cost')}
                testId="rb-cos"
              />
              <BreakdownRow label="± Management (At Cost)" value={rollup.management_value} onClick={() => setBreakdownKey('management')} testId="rb-management" subtle />
              <button
                type="button"
                onClick={() => setBreakdownKey('expected_closing')}
                data-testid="rb-expected-row"
                className="w-full border-t pt-2 mt-2 flex justify-between items-center font-semibold text-sm text-left rounded hover:bg-slate-50 transition-colors"
              >
                <span>= Expected Closing (At Cost)</span>
                <span className="tabular-nums underline-offset-4 text-slate-900">{fmtCurrency(rollup.expected_closing_value)}</span>
              </button>
              <button
                type="button"
                onClick={() => setBreakdownKey('actual_closing')}
                data-testid="rb-actual-row"
                className="w-full flex justify-between items-center font-medium pl-3 text-sm text-left rounded hover:bg-slate-50 transition-colors"
              >
                <span className="text-muted-foreground">Actual Closing counted (At Cost)</span>
                <span className="tabular-nums underline-offset-4 text-slate-700">{fmtCurrency(rollup.total_value)}</span>
              </button>
              <div className={`border-t pt-1 mt-1 flex justify-between items-center font-medium text-sm ${(rollup.stocktake_result_legacy ?? 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="rb-cost-result">
                <span>= Stock Result at Cost (Actual − Expected)</span>
                <span className="tabular-nums">{fmtCurrency(rollup.stocktake_result_legacy ?? rollup.stocktake_result ?? 0)}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-muted-foreground pl-3" data-testid="rb-legacy-headline">
                <span>Legacy result (cost result − Management retail Inc VAT)</span>
                <span className="tabular-nums">{fmtCurrency(rollup.stocktake_result_legacy_adjusted ?? 0)}</span>
              </div>

              <h4 className="text-xs font-semibold uppercase tracking-wide text-primary border-b pb-1 mt-4">Part 2 · Sales Reconciliation (Inc VAT)</h4>
              {(() => {
                // Same receipts basis as the single-location panel: Gross Estimated Receipts,
                // + Allowances, − Management retail = receipts-based result (never the cost result).
                const actR = rollup.accounts_sales_incl_vat ?? 0;
                const allowIncl = rollup.allowance_value || 0;
                const estR = rollup.estimated_receipts_incl_vat_gross
                  ?? (rollup.estimated_receipts_incl_vat != null ? rollup.estimated_receipts_incl_vat + allowIncl : 0);
                const rawR = +(actR - estR).toFixed(2);
                const mgmtRetailIncl = rollup.management_sales_incl_vat || 0;
                const finalResult = rollup.stocktake_result_revised_adjusted
                  ?? +(rawR + allowIncl - mgmtRetailIncl + (rollup.wastage_included_value || 0)).toFixed(2);
                return (
                  <>
                    <BreakdownRow label="Actual Receipts (Inc VAT)" value={actR} onClick={() => setBreakdownKey('accounts_sales')} testId="rb-actual-receipts" />
                    <BreakdownRow label="− Gross Estimated Receipts (Inc VAT)" tone="orange" value={-estR} onClick={() => setBreakdownKey('estimated_receipts')} testId="rb-estimated-receipts" />
                    <div className={`border-t pt-1 mt-1 flex justify-between items-center font-medium text-sm ${rawR < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="rb-raw-result">
                      <span>= Raw Surplus / (Shortage)</span>
                      <span className="tabular-nums">{fmtCurrency(rawR)}</span>
                    </div>
                    <BreakdownRow label="+ Allowances (Inc VAT)" value={allowIncl} onClick={() => setBreakdownKey('allowance')} testId="rb-allowance" subtle />
                    <BreakdownRow label="± Management Retail (Inc VAT)" tone="orange" value={-mgmtRetailIncl} onClick={() => setBreakdownKey('management')} testId="rb-mgmt-retail" />
                    <button
                      type="button"
                      onClick={() => setBreakdownKey('result')}
                      data-testid="rb-stocktake-result"
                      className={`w-full border-t pt-2 mt-2 flex justify-between items-center font-bold text-base text-left rounded hover:bg-slate-50 transition-colors ${finalResult < 0 ? 'text-red-600' : 'text-emerald-600'}`}
                    >
                      <span>= Stocktake Result — receipts-based (Inc VAT)</span>
                      <span className="tabular-nums underline-offset-4">{fmtCurrency(finalResult)}</span>
                    </button>
                  </>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI block — same shape and depth as single-location Calculate panel.
          iter423 — each tile is now clickable and pops a breakdown dialog
          with the academic formula, a worked-example substitution, AND a
          per-location contribution table so users can trace exactly how
          the headline figure was assembled. */}
      {(mode === 'consolidated' || selectedIds.length > 0) && (
        <Card className="border-indigo-200" data-testid="results-kpi-block">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {mode === 'consolidated' ? 'Consolidated' : (singleLocation ? singleLocation.location_name : 'Selected Locations')} — Stock Audit Result
              <Badge
                variant="outline"
                className={`text-[10px] font-medium ${isRevised ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-50 text-slate-600'}`}
                data-testid="results-methodology-badge"
              >
                {isRevised ? 'Actual Receipts − Est. Receipts + Allow − Mgmt' : 'Legacy — PLU-led'}
              </Badge>
              <span className="text-[10px] text-muted-foreground ml-auto">
                {visibleLocations.length} location{visibleLocations.length !== 1 ? 's' : ''} contributing · click any tile for breakdown
              </span>
            </CardTitle>
            <CardDescription className="text-xs">
              {mode === 'consolidated'
                ? `Inter-location transfers eliminated. ${hasCellar ? 'Deliveries/credits from cellar; sales from outlets.' : 'All outlets aggregated.'}`
                : `Rolled up across ${visibleLocations.length} location${visibleLocations.length !== 1 ? 's' : ''}. Inter-location transfers between the selected locations net to zero; outbound transfers to unselected locations stay in the math.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Movement strip — Opening + Deliveries − Credits − Effective Outflow + Mgmt → Expected vs Actual */}
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3 text-xs">
              <FlowTile label="Opening" value={fmtCurrency(rollup.opening_stock_value)} tone="slate" onClick={() => setBreakdownKey('opening')} testId="flow-opening" />
              <FlowTile label="+ Deliveries" value={fmtCurrency(rollup.deliveries_value)} tone="emerald" onClick={() => setBreakdownKey('deliveries')} testId="flow-deliveries" />
              <FlowTile label="− Credits" value={fmtCurrency(rollup.credits_value)} tone="red" onClick={() => setBreakdownKey('credits')} testId="flow-credits" />
              <FlowTile
                label={hasCellar && mode === 'consolidated' ? '− Sales (outlets)' : '− Cost of Sales (PLU)'}
                value={fmtCurrency(rollup.sales_at_cost)}
                tone="orange"
                onClick={() => setBreakdownKey('sales_at_cost')}
                testId="flow-cos"
              />
              <FlowTile label="+ Management" value={fmtCurrency(rollup.management_value)} tone="amber" onClick={() => setBreakdownKey('management')} testId="flow-management" />
              <FlowTile label="− Allowance" value={fmtCurrency(rollup.allowance_value)} tone="rose" onClick={() => setBreakdownKey('allowance')} testId="flow-allowance" />
            </div>

            {/* Row 1 — Receipts comparison + Result (mirror single-loc panel) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <KpiTile
                label="Actual Receipts (incl VAT)"
                value={rollup.accounts_sales_incl_vat != null
                  ? fmtCurrency(rollup.accounts_sales_incl_vat)
                  : (rollup.accounts_sales_excl_vat != null ? fmtCurrency(rollup.accounts_sales_excl_vat) : '—')}
                sub="Till receipts entered on each outlet's Sales step"
                onClick={() => setBreakdownKey('accounts_sales')}
                testId="kpi-actual-receipts"
              />
              <KpiTile
                label="Net Estimated Receipts (incl VAT, after allowances)"
                value={rollup.estimated_receipts_incl_vat != null
                  ? fmtCurrency(rollup.estimated_receipts_incl_vat)
                  : (rollup.estimated_receipts_excl_vat != null ? fmtCurrency(rollup.estimated_receipts_excl_vat) : '—')}
                sub="Σ consumption × selling price"
                onClick={() => setBreakdownKey('estimated_receipts')}
                testId="kpi-estimated-receipts"
              />
              <KpiTile
                label={`Headline Result (${isRevised ? (hasCellar ? 'Σ trading outlets, receipts-based — cellar excluded' : 'receipts-based') : 'Legacy, cost-based'})`}
                value={fmtCurrency(headlineResult)}
                valueClassName={`text-xl font-bold ${resultColor(headlineResult)}`}
                sub={`${(headlineResult ?? 0) >= 0 ? 'Surplus' : 'Deficit'} · ${rollup.line_count} lines · ${isRevised ? 'Revised' : 'Legacy'}`}
                onClick={() => setBreakdownKey('result')}
                highlight={headlineResult > 0 ? 'surplus' : headlineResult < 0 ? 'shortage' : null}
                big
                testId="kpi-result"
              />
            </div>
            {rollup.cellar_physical_variance_excluded_from_headline && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50/60 px-3 py-2 text-xs" data-testid="cellar-physical-variance-excluded-rollup">
                <span className="text-purple-700">Cellar physical variance (at cost) — excluded from this headline</span>
                <span className={`font-semibold tabular-nums ${resultColor(rollup.cellar_physical_variance_at_cost)}`}>{fmtCurrency(rollup.cellar_physical_variance_at_cost)}</span>
              </div>
            )}

            {/* Row 2 — Cost / margin */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
              <KpiTile
                label="Consumption at Cost"
                value={rollup.cost_of_sales_indirect != null ? fmtCurrency(rollup.cost_of_sales_indirect) : '—'}
                sub="Opening + Deliv + TI − Cred − TO − Closing"
                onClick={() => setBreakdownKey('cos_indirect')}
                testId="kpi-consumption-at-cost"
              />
              <KpiTile
                label="Cost of Sales"
                value={rollup.cost_of_sales_indirect != null ? fmtCurrency(rollup.cost_of_sales_indirect) : (rollup.sales_at_cost != null ? fmtCurrency(rollup.sales_at_cost) : '—')}
                sub={isRevised ? 'Consumption at Cost' : 'PLU-led'}
                onClick={() => setBreakdownKey(isRevised ? 'cos_indirect' : 'sales_at_cost')}
                testId="kpi-cos"
              />
              <KpiTile
                label="COS %"
                value={(() => {
                  const cos = rollup.cost_of_sales_indirect;
                  const rev = rollup.accounts_sales_excl_vat ?? rollup.sales_at_selling_excl_vat;
                  if (cos != null && rev > 0) return `${((cos / rev) * 100).toFixed(1)}%`;
                  return '—';
                })()}
                sub={(() => {
                  const cos = rollup.cost_of_sales_indirect;
                  const rev = rollup.accounts_sales_excl_vat ?? rollup.sales_at_selling_excl_vat;
                  if (cos != null && rev != null) return `${fmtCurrency(cos)} / ${fmtCurrency(rev)}`;
                  return 'COS / Revenue';
                })()}
                onClick={() => setBreakdownKey('cos_pct')}
                testId="kpi-cos-pct"
              />
              <KpiTile
                label="Gross Profit"
                value={rollup.headline_gross_profit != null ? fmtCurrency(rollup.headline_gross_profit) : (rollup.gross_profit_legacy != null ? fmtCurrency(rollup.gross_profit_legacy) : '—')}
                sub={rollup.gross_profit_revised != null ? 'Actual Receipts excl VAT − Consumption at Cost' : 'PLU revenue − COS'}
                onClick={() => setBreakdownKey('gp')}
                testId="kpi-gp"
              />
              <KpiTile
                label="GP %"
                value={rollup.headline_gp_pct != null ? `${rollup.headline_gp_pct.toFixed(1)}%` : (rollup.gp_pct_legacy != null ? `${rollup.gp_pct_legacy.toFixed(1)}%` : '—')}
                sub={`GP: ${rollup.headline_gross_profit != null ? fmtCurrency(rollup.headline_gross_profit) : '—'}`}
                onClick={() => setBreakdownKey('gp_pct')}
                testId="kpi-gp-pct"
              />
            </div>

            {/* Row 3 — Operational benchmarks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
              <KpiTile
                label="Yield %"
                value={rollup.yield_pct != null ? `${Number(rollup.yield_pct).toFixed(1)}%` : '—'}
                sub="Actual Receipts ÷ (Gross Est. Receipts + Mgmt)"
                onClick={() => setBreakdownKey('yield_pct')}
                testId="kpi-yield-pct"
              />
              <KpiTile
                label="Stockholding"
                value={rollup.stockholding_days != null ? `${rollup.stockholding_days} days` : '—'}
                sub={`Closing ÷ Avg daily COS${rollup.period_days ? ` · ${rollup.period_days}-day period` : ''}`}
                onClick={() => setBreakdownKey('stockholding')}
                testId="kpi-stockholding"
              />
              <KpiTile
                label="Closing Stock"
                value={rollup.total_value != null ? fmtCurrency(rollup.total_value) : '—'}
                sub="Counted closing stock value"
                onClick={() => setBreakdownKey('closing_stock')}
                testId="kpi-closing-stock"
              />
            </div>

            {/* Expected vs Actual closing — secondary row for transparency */}
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
              <KpiTile
                label="Expected Closing"
                value={fmtCurrency(rollup.expected_closing_value)}
                sub="Opening + Deliveries − Credits − Effective Outflow + Mgmt"
                onClick={() => setBreakdownKey('expected_closing')}
                testId="kpi-expected-closing"
              />
              <KpiTile
                label="Actual Closing"
                value={fmtCurrency(rollup.total_value)}
                sub="From Closing Stock counts"
                onClick={() => setBreakdownKey('actual_closing')}
                testId="kpi-actual-closing"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-location drill-down — only shown when selection is meaningful */}
      {(mode === 'consolidated' || selectedIds.length > 0) && (
        <Card data-testid="results-drilldown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {mode === 'consolidated' ? 'Per-Location Detail' : (singleLocation ? 'Calculation Detail' : 'Selected Locations')}
            </CardTitle>
            <CardDescription className="text-xs">Click to expand and see each step of the calculation.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {visibleLocations.map(loc => (
                <DrilldownRow key={loc.session_id} loc={loc} navigate={navigate} />
              ))}
              {visibleLocations.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">No calculated locations in this selection yet.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transfer Reconciliation — only meaningful for Consolidated */}
      {mode === 'consolidated' && transfer_summary.total_count > 0 && (
        <Card data-testid="results-transfer-reconciliation">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-teal-600" />Internal Transfer Reconciliation</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4 text-xs mb-2">
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">{transfer_summary.posted_count} posted ({fmtCurrency(transfer_summary.posted_value)})</span>
              {transfer_summary.draft_count > 0 && <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">{transfer_summary.draft_count} draft — excluded</span>}
            </div>
            <div className="space-y-1">
              {(transfer_summary.details || []).map(t => (
                <div key={t.id} className="flex items-center justify-between px-2.5 py-1.5 rounded border text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className={`shrink-0 text-[9px] ${t.status === 'posted' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{t.status}</Badge>
                    <span className="font-medium truncate">{t.from_location}</span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-medium truncate">{t.to_location}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-2 text-muted-foreground">
                    <span>{t.transfer_date}</span>
                    <span className="font-medium text-slate-700 tabular-nums">{fmtCurrency(t.total_value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter423 — Breakdown dialog: per-location contribution table +
          formula + worked example, reusing the same dialog component as
          the single-location Calculate panel for visual consistency. */}
      <StockAuditBreakdownDialog
        open={!!breakdownKey}
        onClose={() => setBreakdownKey(null)}
        breakdown={breakdownKey ? buildMultiLocBreakdown(breakdownKey, {
          visibleLocations,
          rollup,
          mode,
          hasCellar,
          productRollup,
          productRollupLoading,
          salesBreakdown,
          selectedLocationIds: visibleLocations.map(l => l.session_id),
        }) : null}
      />
    </div>
  );
}

// iter423-fix-v2 — Compact figure-row used inside the Part 1/Part 2
// Results Breakdown. Mirrors the ClickableFigure pattern from the
// single-location panel so users see the same layout in both places.
function BreakdownRow({ label, value, onClick, testId, tone, subtle }) {
  const colorClass = tone === 'emerald' ? 'text-emerald-600'
    : tone === 'red' ? 'text-red-600'
    : tone === 'orange' ? 'text-orange-600'
    : subtle ? 'text-slate-600' : 'text-slate-900';
  const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) + 0));
  // iter425 — Whole row is clickable (was: only the value button). This
  // ensures `data-testid={testId}` is on the click target so E2E tests
  // and accessibility tooling can drive the breakdown drill-down.
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex justify-between items-center py-1 text-left rounded hover:bg-slate-50 transition-colors focus:outline-none focus:ring-1 focus:ring-slate-400"
    >
      <span className={`text-sm ${colorClass}`}>{label}</span>
      <span
        className={`tabular-nums text-sm font-medium whitespace-nowrap underline-offset-4 group-hover:underline ${colorClass}`}
      >
        {fmt(value)}
      </span>
    </button>
  );
}

// iter423 — Compact KPI tile that mirrors the single-loc Calculate
// panel's Tile component (label / big value / sub line) and is fully
// clickable to pop a breakdown.
function KpiTile({ label, value, sub, onClick, testId, valueClassName, highlight, big }) {
  const bg = highlight === 'surplus' ? 'border-emerald-300 bg-emerald-50/70 hover:bg-emerald-50'
    : highlight === 'shortage' ? 'border-red-300 bg-red-50/70 hover:bg-red-50'
    : 'bg-white hover:border-slate-400';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-lg border px-3 py-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400 hover:shadow-sm ${bg}`}
    >
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={valueClassName || `tabular-nums font-bold ${big ? 'text-xl' : 'text-lg'} text-slate-900`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </button>
  );
}

// iter423 — Compact flow-strip tile (Opening + Deliveries − Credits − …).
// Coloured by directional tone and clickable for breakdown.
function FlowTile({ label, value, tone, onClick, testId }) {
  const tones = {
    slate: 'bg-slate-50 hover:bg-slate-100 text-slate-900',
    emerald: 'bg-emerald-50/80 hover:bg-emerald-100/80 text-emerald-700',
    red: 'bg-red-50/60 hover:bg-red-100/60 text-red-700',
    orange: 'bg-orange-50/60 hover:bg-orange-100/60 text-orange-700',
    amber: 'bg-amber-50/70 hover:bg-amber-100/70 text-amber-700',
    rose: 'bg-rose-50/60 hover:bg-rose-100/60 text-rose-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-lg p-2.5 text-left transition-colors ${tones[tone] || tones.slate}`}
    >
      <p className="text-[10px]">{label}</p>
      <p className="text-sm font-bold tabular-nums">{value}</p>
    </button>
  );
}

// Per-location drill-down row (collapsible). Defined at module scope so
// the multi-loc results panel doesn't re-render every row on each
// expand toggle.
function DrilldownRow({ loc, navigate }) {
  const [open, setOpen] = useState(false);
  // iter423-fix-v3 — Reports dialog accessible directly from each
  // calculated location. Users no longer need to "complete" a session
  // before they can pull Stock On Hand / Estimated vs Till / Stock
  // Audit PDFs — the dialog gates on snapshot presence, not status.
  const [reportsOpen, setReportsOpen] = useState(false);
  const c = loc.calculation;
  const isCellar = loc.location_type === 'cellar';
  const resultColor = (v) => v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-slate-600';
  return (
    <div className={`border rounded-lg overflow-hidden ${isCellar ? 'border-purple-200' : ''}`} data-testid={`results-drilldown-row-${loc.session_id}`}>
      <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50/50" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-2">
          {isCellar ? <Package className="h-3.5 w-3.5 text-purple-600" /> : <MapPin className="h-3.5 w-3.5 text-blue-600" />}
          <span className="text-sm font-medium">{loc.location_name}</span>
          <Badge className={`text-[9px] ${STATUS_COLORS[loc.status] || ''}`}>{loc.status?.replace('_', ' ')}</Badge>
          <span className="text-[10px] text-muted-foreground">{loc.auditor_name}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {c && <span className={`font-medium tabular-nums ${resultColor(c.stocktake_result)}`}>{fmtCurrency(c.stocktake_result)}</span>}
          {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </div>
      {open && c && (
        <div className="px-3 pb-3 border-t bg-slate-50/30">
          <LocationCalcGrid calc={c} locationType={loc.location_type} />
          <div className="flex items-center justify-between mt-2 pt-1 border-t">
            <p className="text-[10px] text-muted-foreground">v{c.version} | {c.line_count} lines | {c.calculated_at ? new Date(c.calculated_at).toLocaleString('en-GB') : ''}</p>
            <div className="flex items-center gap-1">
              {/* iter423-fix-v3 — Reports CTA available the moment a
                  calculation exists. Backend gates on snapshot, not
                  status. */}
              <Button size="sm" variant="ghost" className="text-[10px] gap-1 h-6" onClick={() => setReportsOpen(true)} data-testid={`results-drilldown-reports-${loc.session_id}`}>
                <FileText className="h-3 w-3" />Reports
              </Button>
              <Button size="sm" variant="ghost" className="text-[10px] gap-1 h-6" onClick={() => navigate(`/stocktaking/sessions/${loc.session_id}`)}>
                <Eye className="h-3 w-3" />Open Wizard
              </Button>
            </div>
          </div>
        </div>
      )}
      {open && !c && (
        <div className="px-3 pb-3 border-t">
          <p className="text-xs text-amber-600 py-2">No calculation yet.</p>
          <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => navigate(`/stocktaking/sessions/${loc.session_id}`)}>Open Location</Button>
        </div>
      )}
      {/* Reports dialog mounted at the drill-down row so each location
          opens its own report manifest. iter423-fix-v3. */}
      {reportsOpen && (
        <StocktakeReportsDialog
          open={reportsOpen}
          onOpenChange={setReportsOpen}
          sessionId={loc.session_id}
        />
      )}
    </div>
  );
}

// ── Location Calc Grid — reusable calc display ──────
function LocationCalcGrid({ calc, locationType }) {
  const resultColor = (v) => v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : 'text-slate-600';
  const isCellar = locationType === 'cellar';
  const isOutlet = locationType === 'standard' || locationType === 'outlet';
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-2 text-xs">
        <div><p className="text-[10px] text-muted-foreground">Opening</p><p className="font-medium tabular-nums">{fmtCurrency(calc.opening_stock_value)}</p></div>
        {isCellar ? (
          <>
            <div><p className="text-[10px] text-emerald-600">+ Deliveries</p><p className="font-medium tabular-nums text-emerald-600">{fmtCurrency(calc.deliveries_value)}</p></div>
            <div><p className="text-[10px] text-red-500">- Credits</p><p className="font-medium tabular-nums text-red-500">{fmtCurrency(calc.credits_value)}</p></div>
            <div><p className="text-[10px] text-muted-foreground">- Adjustments</p><p className="font-medium tabular-nums">{fmtCurrency(calc.adjustments_value)}</p></div>
          </>
        ) : isOutlet ? (
          <>
            <div><p className="text-[10px] text-emerald-600">+ From Cellar</p><p className="font-medium tabular-nums text-emerald-600">{fmtCurrency(calc.transfers_in_value)}</p></div>
            <div><p className="text-[10px] text-orange-600">- Returns</p><p className="font-medium tabular-nums text-orange-600">{fmtCurrency(calc.transfers_out_value)}</p></div>
            <div><p className="text-[10px] text-muted-foreground">- Adjustments</p><p className="font-medium tabular-nums">{fmtCurrency(calc.adjustments_value)}</p></div>
          </>
        ) : (
          <>
            <div><p className="text-[10px] text-emerald-600">+ Deliveries</p><p className="font-medium tabular-nums text-emerald-600">{fmtCurrency(calc.deliveries_value)}</p></div>
            <div><p className="text-[10px] text-red-600">- COS</p><p className="font-medium tabular-nums text-red-600">{fmtCurrency(calc.sales_at_cost)}</p></div>
            <div><p className="text-[10px] text-muted-foreground">- Adj</p><p className="font-medium tabular-nums">{fmtCurrency(calc.adjustments_value)}</p></div>
          </>
        )}
      </div>
      {isCellar && (calc.transfers_in_value > 0 || calc.transfers_out_value > 0) && (
        <div className="grid grid-cols-2 gap-2 py-1 text-xs border-t border-dashed">
          <div><p className="text-[10px] text-teal-600">+ Received from Locations</p><p className="font-medium tabular-nums text-teal-600">{fmtCurrency(calc.transfers_in_value)}</p></div>
          <div><p className="text-[10px] text-orange-600">- Issued to Locations</p><p className="font-medium tabular-nums text-orange-600">{fmtCurrency(calc.transfers_out_value)}</p></div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t text-xs">
        <div><p className="text-[10px] text-muted-foreground">Expected</p><p className="font-medium tabular-nums">{fmtCurrency(calc.expected_closing_value)}</p></div>
        <div><p className="text-[10px] text-muted-foreground">Actual</p><p className="font-medium tabular-nums">{fmtCurrency(calc.total_value)}</p></div>
        <div><p className="text-[10px] text-muted-foreground">Result</p><p className={`font-medium tabular-nums ${resultColor(calc.stocktake_result)}`}>{fmtCurrency(calc.stocktake_result)}</p></div>
      </div>
    </div>
  );
}

// ── Zombie Session Rescue ──────────────────────────
// When the parent session was flagged as multi-location but no child
// sessions were created (the legacy create-flow bug pre-iter408), this
// panel lets the auditor pick locations and (re)run enable-multi-location
// without having to delete and recreate the session.
function ZombieRescuePanel({ session, auditors, onRescued }) {
  const { toast } = useToast();
  const [cfg, setCfg] = useState({ location_ids: [], auditor_assignments: {} });
  const [mode, setMode] = useState(session?.audit_mode || 'multi_with_cellar');
  const [saving, setSaving] = useState(false);

  const handleEnable = async () => {
    if (!cfg.location_ids || cfg.location_ids.length < 1) {
      toast({ title: 'Pick at least one location', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await multiLocationService.enableMultiLocation(session.id, {
        audit_mode: mode,
        location_ids: cfg.location_ids,
        auditor_assignments: cfg.auditor_assignments,
      });
      toast({
        title: 'Multi-Location Enabled',
        description: `${cfg.location_ids.length} location session${cfg.location_ids.length !== 1 ? 's' : ''} created.`,
      });
      onRescued && onRescued();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to enable multi-location'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="zombie-rescue-panel" className="border-amber-300">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />Set Up Multi-Location Audit
        </CardTitle>
        <CardDescription className="text-xs">
          This session is flagged as multi-location but has no child location sessions yet. Pick the locations to include and confirm to spin up the child audits.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Audit mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="h-9 text-sm" data-testid="zombie-rescue-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="multi_with_cellar">Multi-Location with Cellar</SelectItem>
              <SelectItem value="multi_no_cellar">Multi-Location (no Cellar)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MultiLocationSetup
          siteId={session.site_id}
          auditors={auditors}
          onConfigChange={setCfg}
        />
        <div className="flex justify-end pt-1">
          <Button
            onClick={handleEnable}
            disabled={saving || cfg.location_ids.length < 1}
            data-testid="zombie-rescue-confirm"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Enable Multi-Location
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


export function MultiLocationSetup({ siteId, auditors, onConfigChange }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignments, setAssignments] = useState({});
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('standard');
  const [adding, setAdding] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    multiLocationService.listLocations(siteId)
      .then(data => {
        setLocations(data || []);
        setSelectedIds(new Set((data || []).map(l => l.id)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => {
    onConfigChange({ location_ids: Array.from(selectedIds), auditor_assignments: assignments });
  }, [selectedIds, assignments, onConfigChange]);

  const toggleLocation = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const handleAddLocation = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const loc = await multiLocationService.createLocation({ site_id: parseInt(siteId), name: newName.trim(), location_type: newType });
      setLocations(prev => [...prev, loc]);
      setSelectedIds(prev => new Set([...prev, loc.id]));
      setNewName('');
      toast({ title: 'Location Added', description: loc.name });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed'), variant: 'destructive' });
    } finally { setAdding(false); }
  };

  const hasCellar = locations.some(l => l.location_type === 'cellar' && selectedIds.has(l.id));

  if (loading) return <div className="py-3 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />Loading locations...</div>;

  return (
    <div className="space-y-3 border rounded-lg p-3 bg-slate-50/50" data-testid="multi-location-setup">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium flex items-center gap-1.5"><Warehouse className="h-4 w-4 text-indigo-600" />Audit Locations</p>
        {hasCellar && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700">Cellar included</Badge>}
      </div>
      {locations.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No locations defined for this site. Add below.</p>
      ) : (
        <div className="space-y-1.5">
          {locations.map(loc => (
            <div key={loc.id} className={`flex items-center gap-2 px-2 py-1.5 rounded border text-sm ${selectedIds.has(loc.id) ? 'border-indigo-200 bg-indigo-50/50' : 'border-transparent bg-white'}`}>
              <input type="checkbox" checked={selectedIds.has(loc.id)} onChange={() => toggleLocation(loc.id)} className="rounded" data-testid={`loc-checkbox-${loc.id}`} />
              <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${loc.location_type === 'cellar' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {loc.location_type === 'cellar' ? 'C' : 'L'}
              </div>
              <span className="flex-1 font-medium">{loc.name}</span>
              {selectedIds.has(loc.id) && (
                <Select value={String(assignments[loc.id] || 'unassigned')} onValueChange={v => setAssignments(prev => ({ ...prev, [loc.id]: v === 'unassigned' ? null : parseInt(v) }))}>
                  <SelectTrigger className="h-7 w-40 text-xs" data-testid={`loc-auditor-${loc.id}`}><SelectValue placeholder="Auditor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {auditors.map(a => <SelectItem key={a.user_id || a.id} value={String(a.user_id || a.id)}>{a.first_name} {a.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-1 border-t">
        <Input placeholder="New location name" value={newName} onChange={e => setNewName(e.target.value)} className="h-8 text-sm flex-1" data-testid="new-location-name" />
        <Select value={newType} onValueChange={setNewType}>
          <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="standard">Standard</SelectItem><SelectItem value="cellar" disabled={hasCellar}>Cellar{hasCellar ? ' (already added)' : ''}</SelectItem></SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={handleAddLocation} disabled={adding || !newName.trim()} className="h-8" data-testid="add-location-btn">
          {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
        </Button>
      </div>
      {selectedIds.size > 0 && <p className="text-[10px] text-muted-foreground">{selectedIds.size} location{selectedIds.size !== 1 ? 's' : ''} selected{hasCellar && ' (including cellar)'}</p>}
    </div>
  );
}
